import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import Button from '../../common/Button';
import PatientService from '../../../services/patientService';
import OrganizationService from '../../../services/organizationService';
import PatientSectionHeader from '../common/PatientSectionHeader';
import { generateMedicalHistoryPDF } from '../../../utils/pdfGenerator';
import { defaultQuestionnaire, mergeQuestionnaire } from '../../../utils/questionnaireUtils';
import { useUserAutoCreation } from '../../../hooks/useUserAutoCreation';
import MedicalQuestionnaire from './MedicalQuestionnaire';
import PhysicalExam from './PhysicalExam';
import VitalSigns from './VitalSigns';
import SystemsReview from './SystemsReview';
import './MedicalHistory.css';
import Modal from '../../common/Modal';

const MedicalHistory = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const { userProfile } = useUserAutoCreation();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [error, setError] = useState(null);
  const [content, setContent] = useState('');
  const [initialContent, setInitialContent] = useState('');
  const [questionnaire, setQuestionnaire] = useState(defaultQuestionnaire);
  const [initialQuestionnaire, setInitialQuestionnaire] = useState(null);
  const [physicalExam, setPhysicalExam] = useState({
    labios: '',
    lengua: '',
    carrillos: '',
    paladarDuro: '',
    paladarBlando: '',
    cabezaYCuello: '',
    pisoDeBoca: '',
    glandSalivales: '',
    ganglios: '',
    parodonto: '',
    oclusion: '',
    atm: ''
  });
  const [initialPhysicalExam, setInitialPhysicalExam] = useState(null);
  const [vitalSigns, setVitalSigns] = useState({
    peso: '',
    talla: '',
    temperatura: '',
    tensionArterial: '',
    frecuenciaCardiaca: '',
    glicemia: '',
    frecuenciaRespiratoria: ''
  });
  const [initialVitalSigns, setInitialVitalSigns] = useState(null);
  const [systemsReview, setSystemsReview] = useState({
    aparatoDigestivo: { answer: null, details: '' },
    aparatoRespiratorio: { answer: null, details: '' },
    aparatoCardiovascular: { answer: null, details: '' },
    aparatoGenitourinario: { answer: null, details: '' },
    sistemaEndocrino: { answer: null, details: '' },
    sistemaHematopoyetico: { answer: null, details: '' },
    sistemaNervioso: { answer: null, details: '' },
    sistemaMusculoEsqueletico: { answer: null, details: '' },
    aparatoTegumentario: { answer: null, details: '' }
  });
  const [initialSystemsReview, setInitialSystemsReview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [patient, setPatient] = useState(null);
  const [clinic, setClinic] = useState(null);

  // Convert any existing HTML to plain text when loading
  const htmlToText = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const token = auth.user?.access_token;
        const userId = auth.user?.profile?.sub;
        
        // Load patient data
        const patientData = await PatientService.getPatientById(patientId, token);
        setPatient(patientData);
        
        // Load clinic information (including logo)
        try {
          const clinicData = await OrganizationService.getCurrentUserOrganization(userId, token);
          setClinic(clinicData);
        } catch (clinicError) {
          console.warn('Could not load clinic information:', clinicError);
          // Continue without clinic data - PDF will use default
        }

        const value = patientData?.medicalHistory || '';
        const text = htmlToText(value);
        setContent(text);
        setInitialContent(text);
        
        // Load questionnaire data, merging with defaults to ensure all fields are present
        const questionnaireData = mergeQuestionnaire(patientData?.medicalQuestionnaire);
        setQuestionnaire(questionnaireData);
        setInitialQuestionnaire(JSON.stringify(questionnaireData));
        
        // Load physical exam data with default structure
        const defaultPhysicalExam = {
          labios: '',
          lengua: '',
          carrillos: '',
          paladarDuro: '',
          paladarBlando: '',
          cabezaYCuello: '',
          pisoDeBoca: '',
          glandSalivales: '',
          ganglios: '',
          parodonto: '',
          oclusion: '',
          atm: ''
        };
        const physicalExamData = patientData?.physicalExam || defaultPhysicalExam;
        setPhysicalExam(physicalExamData);
        setInitialPhysicalExam(JSON.stringify(physicalExamData));
        
        // Load vital signs data with default structure
        const defaultVitalSigns = {
          peso: '',
          talla: '',
          temperatura: '',
          tensionArterial: '',
          frecuenciaCardiaca: '',
          glicemia: '',
          frecuenciaRespiratoria: ''
        };
        const vitalSignsData = patientData?.vitalSigns || defaultVitalSigns;
        setVitalSigns(vitalSignsData);
        setInitialVitalSigns(JSON.stringify(vitalSignsData));
        
        // Load systems review data with default structure
        const defaultSystemsReview = {
          aparatoDigestivo: { answer: null, details: '' },
          aparatoRespiratorio: { answer: null, details: '' },
          aparatoCardiovascular: { answer: null, details: '' },
          aparatoGenitourinario: { answer: null, details: '' },
          sistemaEndocrino: { answer: null, details: '' },
          sistemaHematopoyetico: { answer: null, details: '' },
          sistemaNervioso: { answer: null, details: '' },
          sistemaMusculoEsqueletico: { answer: null, details: '' },
          aparatoTegumentario: { answer: null, details: '' }
        };
        const systemsReviewData = patientData?.systemsReview || defaultSystemsReview;
        setSystemsReview(systemsReviewData);
        setInitialSystemsReview(JSON.stringify(systemsReviewData));
      } catch (e) {
        setError(e.message || 'No se pudo cargar la historia clínica');
      } finally {
        setLoading(false);
      }
    };
    if (patientId && auth.user) load();
  }, [patientId, auth.user]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await PatientService.updatePatient(patientId, { 
        medicalHistory: content,
        medicalQuestionnaire: questionnaire,
        physicalExam: physicalExam,
        vitalSigns: vitalSigns,
        systemsReview: systemsReview
      }, auth.user?.access_token);
      
      // Reload data to refresh initial values after successful save
      const token = auth.user?.access_token;
      const patientData = await PatientService.getPatientById(patientId, token);
      
      // Update initial values for change detection
      const value = patientData?.medicalHistory || '';
      const text = htmlToText(value);
      setInitialContent(text);
      
      const questionnaireData = patientData?.medicalQuestionnaire || questionnaire;
      setInitialQuestionnaire(JSON.stringify(questionnaireData));
      
      const physicalExamData = patientData?.physicalExam || physicalExam;
      setInitialPhysicalExam(JSON.stringify(physicalExamData));
      
      const vitalSignsData = patientData?.vitalSigns || vitalSigns;
      setInitialVitalSigns(JSON.stringify(vitalSignsData));
      
      const systemsReviewData = patientData?.systemsReview || systemsReview;
      setInitialSystemsReview(JSON.stringify(systemsReviewData));
      
    } catch (e) {
      alert(e.message || 'No se pudo guardar el historial');
    } finally {
      setSaving(false);
    }
  };

  const handleGeneratePDF = async () => {
    if (patient) {
      try {
        console.log('handleGeneratePDF: Starting...');
        setGeneratingPDF(true);
        
        // Get user information from user profile state instead of auth context
        const userInfo = {
          name: userProfile?.name,
          professionalId: userProfile?.professionalId || userProfile?.licenseNumber || '',
          email: userProfile?.email || '',
          licenseId: userProfile?.licenseId || '',
          specialization: userProfile?.specialization,
          title: userProfile?.title
        };
        
        await generateMedicalHistoryPDF(patient, content, clinic, userInfo, questionnaire, physicalExam, vitalSigns, systemsReview, clinic?.logoViewUrl);
        console.log('handleGeneratePDF: PDF generation completed successfully');
      } catch (error) {
        console.error('handleGeneratePDF: Error generating PDF:', error);
        alert('Error al generar el PDF. Por favor, inténtalo de nuevo.');
      } finally {
        console.log('handleGeneratePDF: Resetting button state');
        setGeneratingPDF(false);
      }
    }
  };

  const handleBack = () => {
    const questionnaireChanged = JSON.stringify(questionnaire) !== initialQuestionnaire;
    const physicalExamChanged = JSON.stringify(physicalExam) !== initialPhysicalExam;
    const vitalSignsChanged = JSON.stringify(vitalSigns) !== initialVitalSigns;
    const systemsReviewChanged = JSON.stringify(systemsReview) !== initialSystemsReview;
    if (content !== initialContent || questionnaireChanged || physicalExamChanged || vitalSignsChanged || systemsReviewChanged) {
      setConfirmOpen(true);
    } else {
      navigate(`/patients/${patientId}`);
    }
  };

  const discardChanges = () => {
    setConfirmOpen(false);
    navigate(`/patients/${patientId}`);
  };

  const cancelDiscard = () => setConfirmOpen(false);

  if (loading) return <div className="medical-history-loading"><div className="loading-spinner"></div></div>;
  if (error) return <div className="medical-history-error">{error}</div>;

  return (
    <div className="medical-history-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Historia clínica"
        onBackClick={handleBack}
        actionButton={
          <div style={{ display: 'flex', gap: '8px' }}>
            <Button 
              variant="outline" 
              icon="fas fa-file-pdf" 
              onClick={handleGeneratePDF}
              disabled={!patient || generatingPDF}
            >
              {generatingPDF ? 'Generando...' : 'Generar PDF'}
            </Button>
            <Button 
              variant="primary" 
              icon="fas fa-save" 
              onClick={handleSave} 
              disabled={saving}
            >
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        }
      />

      {/* Medical Questionnaire */}
      <MedicalQuestionnaire 
        questionnaire={questionnaire}
        onChange={setQuestionnaire}
      />

      {/* Physical Examination */}
      <PhysicalExam 
        physicalExam={physicalExam}
        onChange={setPhysicalExam}
      />

      {/* Vital Signs */}
      <VitalSigns 
        vitalSigns={vitalSigns}
        onChange={setVitalSigns}
      />

      {/* Systems Review */}
      <SystemsReview 
        systemsReview={systemsReview}
        onChange={setSystemsReview}
      />

      {/* Free-form Medical History */}
      <div className="history-textarea-section">
        <h3 className="section-header">Notas Adicionales de Historia Clínica</h3>
        <div className="mh-editor-wrapper">
        {/* Plain textarea (no formatting) */}
        <textarea
          className="mh-editor"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Escribe la historia clínica del paciente..."
          rows={14}
          style={{ width: '100%', border: 'none', resize: 'vertical' }}
        />
        </div>
      </div>

      <p className="mh-note">La información de la historia clínica se guarda dentro del expediente del paciente.</p>

      {/* Action buttons at the bottom */}
      <div className="mh-bottom-actions">
        <Button 
          variant="outline" 
          icon="fas fa-file-pdf" 
          onClick={handleGeneratePDF}
          disabled={!patient || generatingPDF}
        >
          {generatingPDF ? 'Generando...' : 'Generar PDF'}
        </Button>
        <Button 
          variant="primary" 
          icon="fas fa-save" 
          onClick={handleSave} 
          disabled={saving}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>

      {/* Confirmation modal for unsaved changes */}
      <Modal
        isOpen={confirmOpen}
        title="Descartar cambios"
        onClose={cancelDiscard}
        footer={
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', width: '100%' }}>
            <Button variant="outline" onClick={cancelDiscard}>Cancelar</Button>
            <Button variant="primary" onClick={discardChanges}>Descartar</Button>
          </div>
        }
      >
        <p>Hay cambios sin guardar. ¿Seguro que deseas salir sin guardar?</p>
      </Modal>
    </div>
  );
};

export default MedicalHistory;
